import { defineStore } from 'pinia'
import { getLocale } from '@didi/gettext'
import { env, ENV_kEYS } from '@/utils'
import { login } from '@/plugins/plugin-sso-login'
import { getApplyRoleList } from '@/pages/overview/service'
import { getAllSideList } from '@/pages/workshop/service'
import { getUserAuthCountry, getUserInfoV1 } from '@/services'
import { gtrCollectTrackCommonData, gtrTrackLogin } from '@/utils/gattaran-track-event'
import { showUpdateInfo } from '@/utils/cache-opt'
import lodash from 'lodash'
const storeKey = 'gattaran-workshop-store-app'
const useAppStore = defineStore(storeKey, {
  // 所有数据持久化
  // persist: true,
  // 持久化存储插件其他配置
  persist: [
    {
      // 修改存储中使用的键名称，默认为当前 Store的 id
      key: storeKey,
      // 修改为 sessionStorage，默认为 localStorage
      storage: window.localStorage,
      // 部分持久化状态的点符号路径数组，[]意味着没有状态被持久化(默认为undefined，持久化整个状态)
      paths: ['userInfo', 'sideInfo', 'userAuthCountry'],
      afterRestore: ctx => {
        console.log(`--> just restored '${ctx.store.$id}'`)
      },
    },
  ],
  // 推荐使用 完整类型推断的箭头函数
  state: () => {
    return {
      // 用户信息
      userInfo: {}, // 为了组装featureAuth，存store的话性能消耗大，建议从env.get('userInfo')取。
      // getUserAuthCountry
      userAuthCountry: [],
      // 角色列表
      roleList: [],
      // 业务线信息
      sideInfo: [],
      moduleInfo: {},
      // 子应用埋点时间(HTML加载)
      loadTime: {},
      microAppLoading: false,
      // 激活应用信息
      // name: string
      // activeRule: string
      // entry: string
      // appLocation: string|regex[]
      // container: string
      activeMicroAppConfig: null,
      isMobile: false, //  预留
    }
  },
  actions: {
    setIsMobile(data) {
      this.isMobile = data
    },
    setModuleInfo(data) {
      this.moduleInfo = data
    },
    setMicroAppLoadingStauts(status) {
      this.microAppLoading = status === 'loading'
    },
    setActiveMicroAppConfig(config) {
      this.activeMicroAppConfig = config
    },
    /**
     * 初始化用户国家列表
     */
    /**
     * 初始化用户国家列表
     * 1. 优先从缓存（store）中获取用户国家列表，如果有则异步刷新数据
     * 2. 如果没有缓存，则请求接口获取
     * 3. 获取到的数据会同步到env和store
     * 4. 支持检测数据是否有更新（checkUpdateInfo）
     */
    async initUserAuthCountry() {
      // 内部方法：获取用户国家列表
      const getUserAuthCountryList = async (isCache = false) => {
        try {
          // 调用接口获取用户国家列表
          const list = await getUserAuthCountry()
          // 可以热更新
          // 更新store中的userAuthCountry
          this.userAuthCountry = list || []
          return true
        } catch (e) {
          // 捕获异常，打印警告，并重置userAuthCountry为空
          console.warn(e)
          this.userAuthCountry = []
          return false
        }
      }
      // 如果store中已有用户国家列表，优先用缓存并异步刷新
      if (this.userAuthCountry.length > 0) {
        getUserAuthCountryList()
        return true
      }
      // 没有缓存则直接请求接口
      return await getUserAuthCountryList()
    },
    // 没有用到
    async initRoleList() {
      const getRoleList = async (isCache = false) => {
        try {
          const list = await getApplyRoleList()
          console.log('log => initRoleList list', list)
          this.roleList = list || []
          return true
        } catch (e) {
          console.warn(e)
          this.roleList = []
          return false
        }
      }
      if (this.roleList.length > 0) {
        getRoleList(true)
        return
      }
      await getRoleList()
    },
    // 初始化侧边信息
    async initSidesList() {
      const getSideInfo = async () => {
        try {
          const list = await getAllSideList()
          this.sideInfo = [...list] || []
        } catch (e) {
          console.warn(e)
          this.sideInfo = []
        }
      }
      // 缓存有数据，直接返回
      if (this.sideInfo.length > 0) {
        // 异步获取数据
        getSideInfo()
        return
      }
      await getSideInfo()
    },
    initUserInfo() {
      // 登录埋点 开始 结束
      const trackData = {
        start() {
          // 缓存登录开始时间
          sessionStorage.setItem('loginStartTime', new Date().getTime())
        },
        end() {
          gtrCollectTrackCommonData({
            request_end_time: new Date().getTime(),
          })
          // 如果缓存里有登录起始时间，说明此次操作是登录成功后的回调
          if (sessionStorage.getItem('loginStartTime')) {
            // 缓存登录完成时间
            gtrTrackLogin({
              loginStartTime: sessionStorage.getItem('loginStartTime'),
              loginReadyTime: new Date().getTime(),
            })
            // 清空缓存内的登录起始时间
            sessionStorage.removeItem('loginStartTime')
          }
        },
      }
      // 登录成功后的回调
      const loginSuccess = data => {
        // 存储locale
        if (!data.locale) {
          data.locale = getLocale()
        }
        // 存储用户信息
        this.userInfo = data
        env.set(ENV_kEYS.userInfo, data)
      }
      // 缓存优化
      if (this.userInfo.id) {
        loginSuccess(this.userInfo)
        trackData.end()
        // 异步获取用户信息，更新缓存
        getUserInfoV1().then(res => {
          console.log('log => getUserInfoV1 res', res)
          const { errno, data } = res
          if (+errno === 0 && data) {
            if (checkUserInfoUpdate(data, this.userInfo)) {
              showUpdateInfo('用户信息有更新', true)
            }
            loginSuccess(data)
          }
        })
        return Promise.resolve(this.userInfo)
      }
      return getUserInfoV1().then(res => {
        const { errno, data } = res
        if (errno === 10000) {
          trackData.start()
          login(data)
          return Promise.reject(res)
        }
        if (+errno === 0 && data) {
          loginSuccess(data)
          trackData.end()
          return data
        }
        return Promise.reject(res)
      })
    },
    switchCountry() {
      this.userInfo = {}
      this.sideInfo = []
      this.userAuthCountry = []
    },
    clearStore() {
      this.userInfo = {}
      this.sideInfo = []
      this.userAuthCountry = []
      localStorage.removeItem(storeKey)
    },
  },
})

export default useAppStore

function checkUserInfoUpdate(newInfo, oldInfo) {
  const keys = ['country', 'locale', 'timezone', 'id']
  // 如果三者有为空的，说明是新用户。无需校验
  if (!newInfo.locale || !newInfo.timezone || !newInfo.country) {
    return false
  }
  return !lodash.isEqual(lodash.pick(lodash.cloneDeep(oldInfo), keys), lodash.pick(newInfo, keys))
}
